import { useEffect, useRef, useState } from 'react';

const DEFAULT_MS_PER_CHAR = 5;
const MAX_TYPEWRITER_DURATION_MS = 8_000;
const MIN_STEP_INTERVAL_MS = 4;

type UseAgentMessageTypewriterOptions = {
    onComplete?: () => void;
};

/**
 * Reveals `fullText` progressively for a “typing” effect. When `enabled` is false, shows full text immediately.
 */
export function useAgentMessageTypewriter(
    fullText: string,
    enabled: boolean,
    options?: UseAgentMessageTypewriterOptions
): { displayedText: string; isComplete: boolean } {
    const [length, setLength] = useState(() => (enabled ? 0 : fullText.length));
    const onCompleteRef = useRef(options?.onComplete);
    const lengthRef = useRef(length);
    onCompleteRef.current = options?.onComplete;
    lengthRef.current = length;

    useEffect(() => {
        if (!enabled) {
            if (lengthRef.current < fullText.length) {
                onCompleteRef.current?.();
            }
            setLength(fullText.length);
            return;
        }

        if (!fullText.length) {
            setLength(0);
            onCompleteRef.current?.();
            return;
        }

        setLength(0);

        const totalAllowedSteps = Math.max(
            1,
            Math.floor(MAX_TYPEWRITER_DURATION_MS / MIN_STEP_INTERVAL_MS)
        );
        const charsPerStep = Math.max(1, Math.ceil(fullText.length / totalAllowedSteps));
        const msPerStep = Math.min(
            DEFAULT_MS_PER_CHAR,
            Math.max(
                MIN_STEP_INTERVAL_MS,
                Math.floor(MAX_TYPEWRITER_DURATION_MS / totalAllowedSteps)
            )
        );

        let index = 0;
        let lastTick = performance.now();
        let raf = 0;

        const tick = (now: number) => {
            if (now - lastTick >= msPerStep) {
                lastTick = now;
                index += charsPerStep;
                const next = Math.min(index, fullText.length);
                setLength(next);

                if (next >= fullText.length) {
                    onCompleteRef.current?.();
                    return;
                }
            }
            raf = requestAnimationFrame(tick);
        };

        raf = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(raf);
        };
    }, [fullText, enabled]);

    const isComplete = length >= fullText.length;
    return {
        displayedText: fullText.slice(0, length),
        isComplete
    };
}
